import { post } from '../../networking';
import dataDogLogger from '../datadog';
import { v4 } from 'uuid';
import { COOKIE_KEYS, normalizeCountry, normalizeEmail, normalizeName, sha256Hash } from './utils';
import localeCode from 'locale-code';
import { isEmpty } from 'lodash';
import { MED_PRODUCT_TRACKING_KEY } from '../../logic/meds-tracking';

const ENVIRONMENT = process.env.NEXT_PUBLIC_NENV;
const trackingEnvironments = ['production'];

export enum TiktokPixelAudience {
    DIGITAL = 'digital',
    MED = 'med'
}
export const TIKTOK_DIGITAL_PIXEL_ID = 'C1O95A6HLSU5AAHCGU7G';
export const TIKTOK_MED_PIXEL_ID = 'D8ET5H3C77U3L7SRJNF0';

export enum TikTokEvent {
    /* Standard events */
    ADD_PAYMENT_INFO = 'AddPaymentInfo',
    ADD_TO_CART = 'AddToCart',
    ADD_TO_WISHLIST = 'AddToWishlist',
    APPLICATION_APPROVAL = 'ApplicationApproval',
    COMPLETE_REGISTRATION = 'CompleteRegistration',
    CONTACT = 'Contact',
    CUSTOMIZE_PRODUCT = 'CustomizeProduct',
    DOWNLOAD = 'Download',
    FIND_LOCATION = 'FindLocation',
    INITIATE_CHECKOUT = 'InitiateCheckout',
    PURCHASE = 'Purchase',
    SCHEDULE = 'Schedule',
    SEARCH = 'Search',
    START_TRIAL = 'StartTrial',
    SUBMIT_APPLICATION = 'SubmitApplication',
    SUBMIT_FORM = 'SubmitForm',
    SUBSCRIBE = 'Subscribe',
    VIEW_CONTENT = 'ViewContent',
    /* Custom events */
    START_NO_CC_TRIAL_DIRECT = 'StartNoCCTrialDirect',
    SUNNYSIDE_M_2 = 'Sunnyside_M_2', // M2 - Med provider review checkout success event
    SUNNYSIDE_M_1 = 'Sunnyside_M_1' // M1 - Med paid subscription event
}

type TikTokEventProperties = {
    price?: number,
    value?: number,
    quantity?: number,
    currency?: string,
    description?: string,
    content_ids?: string | string[],
    content_type?: string,
    contents?: {
        content_id: string,
        quantity: number,
        price: number
    }[],
    product_line?: MED_PRODUCT_TRACKING_KEY,
    funnel_id?: string
}

type TikTokClientSidePayload = TikTokEventProperties & {
    event_id: string
}

type TikTokClientSideUser = {
    email?: string,
    phone?: string,
    external_id?: string
}

type TikTokServerSideUser = TikTokClientSideUser & {
    first_name?: string,
    last_name?: string,
    country?: string,
    user_agent?: string,
    locale?: string,
    ip?: string,
    ttclid?: string,
    ttp?: string
}

type TikTokServerSidePayload = {
    event_source: 'web',
    event_source_id: string,
    test_event_code?: string,
    data: [{
        event: TikTokEvent,
        event_time: number,
        event_id: string,
        user: TikTokServerSideUser,
        page: {
            url: string,
            referrer: string,
        },
        properties?: TikTokEventProperties
    }]
}

const tikTokClientSideProxy = (user: TikTokClientSideUser, eventName: TikTokEvent, properties: TikTokClientSidePayload) => {
    const ttq = window && window['ttq'];
    if (typeof ttq !== 'object') {
        dataDogLogger('error', `TikTok Pixel not initialized when firing event ${eventName}`);
        return;
    }

    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('TikTok Pixel', eventName, properties);
        return;
    }

    ttq.identify(user);
    ttq.track(eventName, properties);
};

const tikTokServerSideProxy = async (eventPayload: TikTokServerSidePayload) => {
    if(!trackingEnvironments.includes(ENVIRONMENT)) {
        console.log('TikTok CAPI', eventPayload.data[0].event, eventPayload);
        return;
    }

    await post('tracking/tik-tok', eventPayload);
};

export default async ({
    pixelAudience = TiktokPixelAudience.DIGITAL,
    event_name,
    user,
    url,
    referrer,
    cookies,
    properties = {}
}: {
    pixelAudience?: TiktokPixelAudience,
    event_name: TikTokEvent,
    user: any,
    url?: string,
    referrer?: string,
    cookies?: any,
    properties?: TikTokEventProperties
}) => {
    const event_id = v4();
    const locale = Intl.DateTimeFormat().resolvedOptions().locale;
    const countryCode = localeCode.getCountryCode(locale);

    const clientSideUser: TikTokClientSideUser = {
        ...(user.email ? { email: sha256Hash(normalizeEmail(user.email)) } : {}),
        ...(user.phone ? { phone: sha256Hash(user.phone) } : {}),
        ...(user.id ? { external_id: sha256Hash(user.id) } : {})
    };

    const clientSideParameters = {
        event_id,
        ...properties
    };

    tikTokClientSideProxy(clientSideUser, event_name, clientSideParameters);

    const serverSideUser: TikTokServerSideUser = {
        ...clientSideUser,
        ...(user.givenName ? { first_name: sha256Hash(normalizeName(user.givenName)) } : {}),
        ...(user.familyName ? { last_name: sha256Hash(normalizeName(user.familyName)) } : {}),
        ...(countryCode ? { country: normalizeCountry(countryCode) } : {}),
        ...(window.navigator.userAgent ? { user_agent: window.navigator.userAgent } : {}),
        ...(locale ? { locale } : {}),
    };

    // insert ttclid and _ttp if exist
    if(cookies) {
        if (cookies[COOKIE_KEYS.TIKTOK_CLICK_ID]) {
            serverSideUser.ttclid = cookies[COOKIE_KEYS.TIKTOK_CLICK_ID];
        }
        if (cookies[COOKIE_KEYS.TIKTOK_COOKIE1]) {
            serverSideUser.ttp = cookies[COOKIE_KEYS.TIKTOK_COOKIE1];
        }
    }

    const serverSidePayload: TikTokServerSidePayload = {
        event_source: 'web',
        event_source_id: TIKTOK_DIGITAL_PIXEL_ID,
        data: [{
            event: event_name,
            event_time: Math.floor(Date.now() / 1000),
            event_id,
            user: serverSideUser,
            page: {
                url,
                referrer,
            }
        }]
    };

    if (!isEmpty(properties)) serverSidePayload.data[0].properties = properties;

    tikTokServerSideProxy(serverSidePayload);

    if (pixelAudience === TiktokPixelAudience.MED) {
        const medServerSidePayload = {
            ...serverSidePayload,
            event_source_id: TIKTOK_MED_PIXEL_ID
        };
        tikTokServerSideProxy(medServerSidePayload);
    }
};
